// src/views/hrms/leaves/LeaveList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, CalendarRange } from "lucide-react";

import { getLeaves, updateLeaveStatus } from "services/hrms.api";
import LeaveTable from "views/hrms/leaves/LeaveTable";
import Loading from "components/common/Loading";

/* ---------- helpers ---------- */
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const typeCodeToLabel = (t) => {
  const x = String(t || "").toUpperCase();
  if (x === "CL") return "Casual";
  if (x === "SL") return "Sick";
  if (x === "PL") return "Earned";
  if (x === "WFH") return "WFH";
  return x || "-";
};
const fmt = (d) => {
  const x = new Date(d);
  return isNaN(x)
    ? "-"
    : x.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
};

const PAGE_SIZE = 10;

export default function LeaveList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [from, setFrom] = useState(""); // YYYY-MM-DD
  const [to, setTo] = useState(""); // YYYY-MM-DD
  const [onDate, setOnDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  // pagination
  const [page, setPage] = useState(1);

  // debounced search
  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // ✅ stable normalize
  const normalize = useCallback((arr) => {
    return (arr || []).map((r) => {
      const empObj =
        r.employee && typeof r.employee === "object" ? r.employee : null;

      const employeeName =
        r.employeeName ||
        empObj?.name ||
        [empObj?.firstName, empObj?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        "-";

      const employeeId =
        r.employeeId || empObj?.code || String(r.employee || "");

      const statusRaw = String(r.status || "").toLowerCase();
      const statusLabel =
        statusRaw === "pending" ||
          statusRaw === "approved" ||
          statusRaw === "rejected" ||
          statusRaw === "cancelled"
          ? cap(statusRaw)
          : r.status || "-";

      const typeLabel = r.typeLabel || typeCodeToLabel(r.type);

      return {
        ...r,
        id: r._id || r.id,
        employeeName,
        employeeId,
        statusLabel,
        from: r.from,
        to: r.to || r.from,
        typeLabel,
      };
    });
  }, []);

  // ✅ stable load
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getLeaves();
      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      setRows(normalize(list));
    } catch (e) {
      console.error("[LeaveList] load error:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [normalize]);

  // ✅ hook-safe
  useEffect(() => {
    load();
  }, [load]);

  /* ----- status updates (lowercase to API) with optimistic UI ----- */
  const approve = async (id) => {
    setRows((rows) =>
      rows.map((r) =>
        (r._id || r.id) === id
          ? { ...r, status: "approved", statusLabel: "Approved" }
          : r
      )
    );
    try {
      await updateLeaveStatus(id, "approved");
      load();
    } catch (e) {
      console.error("approve failed", e);
      setRows((rows) =>
        rows.map((r) =>
          (r._id || r.id) === id
            ? { ...r, status: "pending", statusLabel: "Pending" }
            : r
        )
      );
      alert("Failed to approve leave.");
    }
  };

  const reject = async (id) => {
    setRows((rows) =>
      rows.map((r) =>
        (r._id || r.id) === id
          ? { ...r, status: "rejected", statusLabel: "Rejected" }
          : r
      )
    );
    try {
      await updateLeaveStatus(id, "rejected");
      load();
    } catch (e) {
      console.error("reject failed", e);
      setRows((rows) =>
        rows.map((r) =>
          (r._id || r.id) === id
            ? { ...r, status: "pending", statusLabel: "Pending" }
            : r
        )
      );
      alert("Failed to reject leave.");
    }
  };

  /* ----- filters/search ----- */
  const overlapsRange = (lFrom, lTo, fFrom, fTo) => {
    const a1 = new Date(lFrom);
    const a2 = new Date(lTo || lFrom);
    if (!fFrom && !fTo) return true;
    const b1 = fFrom ? new Date(fFrom) : new Date("1970-01-01");
    const b2 = fTo ? new Date(fTo) : new Date("2999-12-31");
    return a1 <= b2 && a2 >= b1;
  };

  const matchesSearch = (row, k) => {
    if (!k) return true;
    const v = k.toLowerCase();
    return (
      (row.employeeName || "").toLowerCase().includes(v) ||
      (row.employeeId || "").toLowerCase().includes(v) ||
      (row.typeLabel || "").toLowerCase().includes(v) ||
      (row.statusLabel || "").toLowerCase().includes(v) ||
      (row.reason || "").toLowerCase().includes(v)
    );
  };

  const filtered = useMemo(() => {
    return rows.filter(
      (r) =>
        matchesSearch(r, qDebounced) && overlapsRange(r.from, r.to, from, to)
    );
  }, [rows, qDebounced, from, to]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1);

  useEffect(() => {
    setPage(1);
  }, [qDebounced, from, to]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filtered.slice(start, end);
  }, [filtered, page]);

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex =
    filtered.length === 0 ? 0 : Math.min(filtered.length, page * PAGE_SIZE);

  const onDateEmployees = useMemo(() => {
    if (!onDate) return [];
    const day = new Date(onDate);
    const list = rows.filter((r) => {
      if (r.statusLabel === "Rejected" || r.statusLabel === "Cancelled")
        return false;
      const a1 = new Date(r.from);
      const a2 = new Date(r.to || r.from);
      return a1 <= day && day <= a2;
    });
    const seen = new Set();
    const uniq = [];
    for (const r of list) {
      const key = r.employeeId || r.employeeName;
      if (key && !seen.has(key)) {
        seen.add(key);
        uniq.push(r);
      }
    }
    return uniq;
  }, [rows, onDate]);

  const pending = rows.filter((r) => r.statusLabel === "Pending").length;
  const approved = rows.filter((r) => r.statusLabel === "Approved").length;
  const rejected = rows.filter((r) => r.statusLabel === "Rejected").length;
  const thisMonth = rows.filter((r) => {
    const d = new Date(r.from);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    );
  }).length;

  if (loading && rows.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <Loading variant="dots" fullscreen />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-2xl font-semibold dark:text-white">Manage Leave</h2>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
        <div className="flex items-center gap-2 dark:border-white/10 dark:text-white">
          <CalendarRange className="h-4 w-4 text-gray-500 dark:text-white" />
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="dark:bg-transparent rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="dark:bg-transparent rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
          />
        </div>

        <div className="relative ml-auto w-full sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search employee / id / type / status…"
            className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 focus:dark:ring-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Stat
          label="Pending"
          value={pending}
          tone="bg-amber-50 text-amber-700"
        />
        <Stat
          label="Approved"
          value={approved}
          tone="bg-blue-50 text-emerald-700"
        />
        <Stat
          label="Rejected"
          value={rejected}
          tone="bg-amber-50 text-rose-700"
        />
        <Stat label="This Month" value={thisMonth} />
        <Stat
          label="On Leave Today"
          value={onDateEmployees.length}
          tone="bg-blue-50 text-blue-700"
        />
      </div>

      {loading ? (
        <TableSkeleton />
      ) : filtered.length ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
          <LeaveTable rows={pagedRows} onApprove={approve} onReject={reject} />

          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 dark:border-white/10 dark:text-white/70 md:flex-row md:items-center md:justify-between">
            <div>
              Showing <span className="font-medium">{startIndex}</span> to{" "}
              <span className="font-medium">{endIndex}</span> of{" "}
              <span className="font-medium">{filtered.length}</span> leaves
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className={`rounded-lg border px-3 py-1 text-xs font-medium ${page <= 1
                    ? "cursor-not-allowed border-gray-200 text-gray-400 dark:border-white/10 dark:text-white/40"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:bg-white/10"
                  }`}
              >
                Previous
              </button>
              <span className="text-xs">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{pageCount}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className={`rounded-lg border px-3 py-1 text-xs font-medium ${page >= pageCount
                    ? "cursor-not-allowed border-gray-200 text-gray-400 dark:border-white/10 dark:text-white/40"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:bg-white/10"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center px-8 py-20 text-center">
          <Search
            className="h-12 w-12 text-gray-300 dark:text-white/20"
            strokeWidth={1.2}
          />
          <h3 className="mt-4 text-lg font-semibold text-gray-800 dark:text-white">
            No leaves found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-white/70">
            Adjust filters or try a different date range.
          </p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 text-sm font-semibold dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700">
          <div className="dark:text-white">Employees on Leave</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:border-white/10">
              Select date
            </span>
            <input
              type="date"
              value={onDate}
              onChange={(e) => setOnDate(e.target.value)}
              className="dark:bg-transparent rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            />
          </div>
        </div>

        {onDateEmployees.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
            <CalendarRange
              className="h-10 w-10 text-gray-300 dark:text-white/20"
              strokeWidth={1.3}
            />
            <h3 className="mt-3 text-sm font-semibold text-gray-700 dark:text-white">
              No employees on leave for this date
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-white/70">
              Pick another date to view scheduled leaves.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/10">
            {onDateEmployees.map((r) => (
              <li
                key={`${r.employeeId}-${r.from}-${r.to}`}
                className="flex flex-col items-start justify-between gap-1 px-4 py-3 sm:flex-row sm:items-center"
              >
                <div>
                  <div className="font-medium text-navy-700 dark:text-white">
                    {r.employeeName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-white">
                    {r.employeeId} • {fmt(r.from)} — {fmt(r.to || r.from)} •{" "}
                    {r.typeLabel}
                  </div>
                </div>
                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700 dark:text-white">
                  {r.statusLabel}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------- tiny UI atoms ---------- */
function Stat({ label, value, tone = "bg-gray-50 text-gray-700" }) {
  return (
    <div
      className={`rounded-2xl border border-gray-200 p-4 dark:border-white/10 ${tone}`}
    >
      <div className="text-sm">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
      <div className="animate-pulse space-y-0">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-12 border-b border-gray-100 bg-gray-50/70 last:border-0 dark:border-white/10 dark:bg-white/[0.03]"
          />
        ))}
      </div>
    </div>
  );
}
